import React, { useState } from 'react';

import {
  Star,
  TrendingUp,
  Users,
  BookOpen,
  Award,
  Heart,
  Brain,
  Target,
} from 'lucide-react';

import { parentPortalApi } from '../services/parentPortalApi';

import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Progress } from './ui/progress';

interface TeacherInsight {
  teacher_id: number;
  teacher_name: string;
  subject: string;
  effectiveness_score: number;
  compatibility_score: number;
  key_strengths: string[];
}

interface TeacherProfile {
  teacher_id: number;
  teacher_name: string;
  subject_area: string;
  overall_effectiveness: number;
  student_engagement_score: number;
  compatibility_score?: number;
  teaching_strengths: string[];
  classroom_style: string[];
  experience_years: number;
  school_ranking_percentile: number;
}

interface TeacherCompatibilityCardProps {
  teacher: TeacherInsight;
  studentId: number;
}

export const TeacherCompatibilityCard: React.FC<
  TeacherCompatibilityCardProps
> = ({ teacher, studentId }) => {
  const [teacherProfile, setTeacherProfile] = useState<TeacherProfile | null>(
    null
  );
  const [loadingProfile, setLoadingProfile] = useState(false);

  const getCompatibilityColor = (score: number) => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 60) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (score >= 40) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getCompatibilityLabel = (score: number) => {
    if (score >= 80) return 'Excellent Match';
    if (score >= 60) return 'Good Match';
    if (score >= 40) return 'Fair Match';
    return 'Challenging Match';
  };

  const getEffectivenessStars = (score: number) => {
    const stars = Math.round(score);
    return Array.from({ length: 4 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < stars ? 'text-yellow-400 fill-current' : 'text-gray-300'
        }`}
      />
    ));
  };

  const fetchTeacherProfile = async () => {
    if (teacherProfile) return; // Already loaded

    try {
      setLoadingProfile(true);
      const profile = await parentPortalApi.getTeacherProfile(
        teacher.teacher_id,
        studentId
      );
      setTeacherProfile(profile);
    } catch (error) {
      console.error('Error fetching teacher profile:', error);
    } finally {
      setLoadingProfile(false);
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{teacher.teacher_name}</CardTitle>
            <CardDescription className="text-base">
              {teacher.subject}
            </CardDescription>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 mb-1">
              {getEffectivenessStars(teacher.effectiveness_score)}
            </div>
            <p className="text-xs text-gray-500">
              {teacher.effectiveness_score.toFixed(1)}/4.0 Rating
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Compatibility Score */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Student Compatibility</span>
            <Badge
              className={getCompatibilityColor(teacher.compatibility_score)}
            >
              {getCompatibilityLabel(teacher.compatibility_score)}
            </Badge>
          </div>
          <div className="flex items-center gap-3">
            <Progress value={teacher.compatibility_score} className="flex-1" />
            <span className="text-sm font-medium min-w-[3rem]">
              {Math.round(teacher.compatibility_score)}%
            </span>
          </div>
        </div>

        {/* Key Strengths */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Teaching Strengths</h4>
          <div className="flex flex-wrap gap-1">
            {teacher.key_strengths.slice(0, 3).map((strength, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {strength}
              </Badge>
            ))}
            {teacher.key_strengths.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{teacher.key_strengths.length - 3} more
              </Badge>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-4 pt-2 border-t">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Users className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium">Engagement</span>
            </div>
            <p className="text-lg font-bold text-blue-600">
              {Math.round((teacher.effectiveness_score / 4) * 100)}%
            </p>
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Award className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium">Experience</span>
            </div>
            <p className="text-lg font-bold text-green-600">
              {teacherProfile ? `${teacherProfile.experience_years} yrs` : '—'}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={fetchTeacherProfile}
              >
                <BookOpen className="h-4 w-4 mr-2" />
                View Profile
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  {teacher.teacher_name} - Teaching Profile
                </DialogTitle>
                <DialogDescription>
                  Comprehensive effectiveness data and compatibility analysis
                </DialogDescription>
              </DialogHeader>

              {loadingProfile ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : teacherProfile ? (
                <TeacherDetailedProfile profile={teacherProfile} />
              ) : (
                <p className="text-center py-8 text-gray-500">
                  Unable to load teacher profile
                </p>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  );
};

interface TeacherDetailedProfileProps {
  profile: TeacherProfile;
}

const TeacherDetailedProfile: React.FC<TeacherDetailedProfileProps> = ({
  profile,
}) => {
  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="text-center p-3 bg-blue-50 rounded-lg">
          <div className="flex items-center justify-center gap-1 mb-1">
            <Award className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-medium">Overall Rating</span>
          </div>
          <p className="text-xl font-bold text-blue-600">
            {profile.overall_effectiveness.toFixed(1)}/4.0
          </p>
        </div>

        <div className="text-center p-3 bg-green-50 rounded-lg">
          <div className="flex items-center justify-center gap-1 mb-1">
            <Users className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium">Engagement</span>
          </div>
          <p className="text-xl font-bold text-green-600">
            {Math.round(profile.student_engagement_score)}%
          </p>
        </div>

        {profile.compatibility_score && (
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <div className="flex items-center justify-center gap-1 mb-1">
              <Heart className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-medium">Child Match</span>
            </div>
            <p className="text-xl font-bold text-purple-600">
              {Math.round(profile.compatibility_score)}%
            </p>
          </div>
        )}

        <div className="text-center p-3 bg-orange-50 rounded-lg">
          <div className="flex items-center justify-center gap-1 mb-1">
            <TrendingUp className="h-4 w-4 text-orange-600" />
            <span className="text-sm font-medium">School Rank</span>
          </div>
          <p className="text-xl font-bold text-orange-600">
            Top {Math.round(100 - profile.school_ranking_percentile)}%
          </p>
        </div>
      </div>

      {/* Teaching Strengths */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Star className="h-5 w-5 text-yellow-500" />
          Teaching Strengths
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {profile.teaching_strengths.map((strength, index) => (
            <div
              key={index}
              className="flex items-center gap-2 p-2 bg-gray-50 rounded"
            >
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-sm">{strength}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Teaching Style */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Brain className="h-5 w-5 text-blue-500" />
          Teaching Style
        </h3>
        <div className="flex flex-wrap gap-2">
          {profile.classroom_style.map((style, index) => (
            <Badge
              key={index}
              variant="outline"
              className="bg-blue-50 text-blue-700"
            >
              {style}
            </Badge>
          ))}
        </div>
      </div>

      {/* Experience & Background */}
      <div>
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-indigo-500" />
          Experience & Background
        </h3>
        <div className="space-y-2 text-sm">
          <p>
            <strong>Years of Experience:</strong> {profile.experience_years}{' '}
            years
          </p>
          <p>
            <strong>Subject Area:</strong> {profile.subject_area}
          </p>
          <p>
            <strong>School Ranking:</strong> Top{' '}
            {Math.round(100 - profile.school_ranking_percentile)}% in the school
          </p>
        </div>
      </div>

      {/* Compatibility Analysis (if available) */}
      {profile.compatibility_score && (
        <div>
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Target className="h-5 w-5 text-purple-500" />
            Student Compatibility Analysis
          </h3>
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">Overall Compatibility</span>
              <span className="text-lg font-bold text-purple-600">
                {Math.round(profile.compatibility_score)}%
              </span>
            </div>
            <Progress value={profile.compatibility_score} className="mb-3" />
            <p className="text-sm text-gray-600">
              This teacher's style aligns well with your child's learning
              preferences and classroom behavior patterns.
            </p>
          </div>
        </div>
      )}

      {/* Recommendations */}
      <div className="p-4 bg-green-50 rounded-lg border-l-4 border-green-500">
        <h4 className="font-semibold text-green-800 mb-2">
          Parent Recommendations
        </h4>
        <ul className="text-sm text-green-700 space-y-1">
          <li>
            • This teacher excels at creating engaging classroom environments
          </li>
          <li>• Consider discussing your child's learning style preferences</li>
          <li>
            • Regular communication about homework and projects is encouraged
          </li>
          <li>• Teacher is responsive to parent feedback and suggestions</li>
        </ul>
      </div>
    </div>
  );
};
